import React, { useState, useEffect, useMemo } from "react";
import axios from "../utils/axios";
import Spinner from "../components/Spinner";
import Config from "../utils/config";
import Modal from "react-modal";
import {
  differenceInMinutes,
  parse,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
} from "date-fns";
import { fr } from "date-fns/locale";
import { Menu, MenuItem, IconButton, Select, FormControl } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaFileExport } from "react-icons/fa";

const customStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    margin: "0",
    padding: "0",
    border: "none",
    borderRadius: "0",
    background: "#f5f5f5",
    overflow: "auto",
    zIndex: 10000,
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
  },
};

const Timesheet = () => {
  const [workingHoursData, setWorkingHoursData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    month: "",
    name: "",
    email: "",
    event: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  useEffect(() => {
    const fetchWorkingHours = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          ...(filters.month && { month: filters.month }),
        });
        const response = await axios.get(
          `${Config.baseURL}/working-hours?${queryParams.toString()}`
        );
        setWorkingHoursData(response.data);
      } catch (error) {
        console.error("Error fetching working hours data:", error);
        toast.error("Erreur lors de la récupération des données");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkingHours();
  }, [filters.month]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const response = await axios.patch(
        `${Config.baseURL}/working-hours/${recordId}`,
        { status: newStatus }
      );

       setWorkingHoursData((prevData) =>
        prevData.map((record) =>
          record._id === recordId ? { ...record, status: newStatus } : record
        )
      );
      toast.success("Statut mis à jour avec succès !");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const filteredData = useMemo(() => {
    return workingHoursData.filter((record) => {
      const { name, email, event } = filters;
      const fullName =
        `${record.agent?.firstName} ${record.agent?.lastName}`.toLowerCase();
      const recordEmail = record.agent?.email?.toLowerCase() || "";
      const recordEventNames = record.workingHours.map((wh) =>
        wh.eventName.toLowerCase()
      );

      if (name && !fullName.includes(name.toLowerCase())) {
        return false;
      }

      if (email && !recordEmail.includes(email.toLowerCase())) {
        return false;
      }

      if (event) {
        const eventFilter = event.toLowerCase();
        const hasEvent = recordEventNames.some((eName) =>
          eName.includes(eventFilter)
        );
        if (!hasEvent) {
          return false;
        }
      }

      return true;
    });
  }, [workingHoursData, filters.name, filters.email, filters.event]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const toggleModal = (record) => {
    setSelectedRecord(record);
    setIsModalOpen((prev) => !prev);
  };

  const handleMenuClick = (event, recordId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(recordId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cet enregistrement ?")
    ) {
      try {
        await axios.delete(`${Config.baseURL}/working-hours/${id}`);
        const updatedData = workingHoursData.filter(
          (record) => record._id !== id
        );
        setWorkingHoursData(updatedData);
        toast.success("Enregistrement supprimé avec succès");
      } catch (error) {
        console.error("Error deleting record:", error);
        toast.error("Erreur lors de la suppression de l'enregistrement");
      }
    }
  };

  const formatTotalHours = (startTime, endTime) => {
    if (typeof startTime !== "string" || typeof endTime !== "string") {
      return "Temps invalide";
    }

    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);
    const start = new Date();
    start.setHours(startHours, startMinutes);

     const end = new Date();
    end.setHours(endHours, endMinutes);

     if (end <= start) {
      end.setDate(end.getDate() + 1);  
    }

     const totalMinutes = differenceInMinutes(end, start);

     if (totalMinutes < 0) {
      return "Temps invalide";
    }

     const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getFrenchMonth = (date) => {
    const months = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return months[date.getMonth()];
  };

  const today = new Date();
  const dayOfMonth = today.getDate();
  let displayDate;
  if (dayOfMonth <= 15) {
    displayDate = today;
  } else {
    displayDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }
  const monthName = getFrenchMonth(displayDate);
  const year = displayDate.getFullYear();

  const generateCalendar = (month, workingHours) => {
    const start = startOfMonth(new Date(month));
    const end = endOfMonth(new Date(month));
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 mt-4">
        {days.map((day) => {
          const dayEvents = workingHours.filter(
            (entry) =>
              entry.date &&
              format(new Date(entry.date), "yyyy-MM-dd") ===
                format(day, "yyyy-MM-dd")
          );
          const hasEvents = dayEvents.length > 0;
          return (
            <div
              key={day.toString()}
              className={`rounded-lg border p-3 ${
                hasEvents
                  ? "bg-white border-gray-200 shadow-sm min-h-0"
                  : "bg-gray-50 border-gray-100 min-h-[100px]"
              }`}
            >
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex-shrink-0">
                {format(day, "dd MMM yyyy", { locale: fr })}
              </p>
              {hasEvents ? (
                <div className="space-y-3">
                  {dayEvents.map((event, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm overflow-visible"
                    >
                      <p className="font-semibold text-gray-900 break-words leading-snug" title={event.eventName}>
                        {event.eventName}
                      </p>
                      <p className="text-gray-600 mt-1.5 text-xs">
                        {event.eventLocation}
                      </p>
                      <p className="text-gray-600 mt-1 text-xs font-medium tabular-nums">
                        {event.startTime} – {event.endTime}
                        <span className="text-gray-700 ml-1">
                          ({formatTotalHours(event.startTime, event.endTime)})
                        </span>
                      </p>
                      {event.comment && (
                        <p className="text-gray-500 mt-1.5 text-xs italic break-words border-t border-gray-100 pt-1.5">
                          {event.comment}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Aucun événement</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const exportToExcel = async () => {
    if (!selectedRecord) {
      toast.error("Aucun enregistrement sélectionné pour l'exportation.");
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Feuille de Temps");

      const headerStyle = {
        font: { bold: true, size: 14 },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFB0C4DE" },
        },
        alignment: { vertical: "middle", horizontal: "center" },
      };

      const agentInfo = {
        Prénom: selectedRecord.agent?.firstName || "N/A",
        Nom: selectedRecord.agent?.lastName || "N/A",
        Email: selectedRecord.agent?.email || "N/A",
        Téléphone: selectedRecord.agent?.telephone || "N/A",
        Poste: selectedRecord.agent?.poste || "N/A",
        Mois: selectedRecord.month || "N/A",
        "Jours Travaillés": selectedRecord.workingHours?.length || 0,
        "Total Heures": (() => {
          if (!selectedRecord.workingHours) return "N/A";
          const totalMinutes = selectedRecord.workingHours.reduce(
            (acc, entry) =>
              acc +
              (entry.startTime && entry.endTime
                ? differenceInMinutes(
                    parse(entry.endTime, "HH:mm", new Date()),
                    parse(entry.startTime, "HH:mm", new Date())
                  )
                : 0),
            0
          );
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${hours}h ${minutes}m`;
        })(),
      };

      const agentInfoHeaders = Object.keys(agentInfo);
      const agentInfoValues = Object.values(agentInfo);

      worksheet.addRow(agentInfoHeaders).eachCell((cell) => {
        cell.style = headerStyle;
      });

      worksheet.addRow(agentInfoValues);
      worksheet.addRow([]);
      const eventHeaders = [
        "Date",
        "Événement",
        "Lieu",
        "Heure de début",
        "Heure de fin",
        "Total",
        "Commentaire",
      ];
      worksheet.addRow(eventHeaders).eachCell((cell) => {
        cell.style = headerStyle;
      });

      selectedRecord.workingHours.forEach((event) => {
        worksheet.addRow([
          format(new Date(event.date), "dd/MM/yyyy"),
          event.eventName || "N/A",
          event.eventLocation || "N/A",
          event.startTime || "N/A",
          event.endTime || "N/A",
          formatTotalHours(event.startTime, event.endTime) || "N/A",
          event.comment || "N/A",
        ]);
      });

      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : "";
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });

      const sanitizeFileName = (name) => {
        return name.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      };

      const agentFirstName = sanitizeFileName(
        selectedRecord.agent?.firstName || "Agent"
      );
      const agentLastName = sanitizeFileName(
        selectedRecord.agent?.lastName || "Info"
      );
      const exportMonth = selectedRecord.month || "unknown_month";

      const fileName = `${agentFirstName}-${agentLastName}-${exportMonth}-heure_de_travail.xlsx`;

      saveAs(blob, fileName);
      toast.success("Exportation réussie !");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Erreur lors de l'exportation.");
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="min-h-screen bg-gray-50 flex flex-col p-4 sm:p-6 overflow-x-hidden w-full min-w-0">
      <header className="flex-shrink-0 px-2 sm:px-6 mt-4 sm:mt-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
          Feuille de Temps des Agents
        </h1>
        <div className="flex justify-between items-center mb-4"></div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:flex sm:flex-wrap sm:gap-4">
            <input
              type="text"
              name="name"
              placeholder="Rechercher par nom..."
              value={filters.name}
              onChange={handleFilterChange}
              className="px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 min-h-[44px] sm:min-h-0 w-full"
            />
            <input
              type="text"
              name="email"
              placeholder="Rechercher par email..."
              value={filters.email}
              onChange={handleFilterChange}
              className="px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 min-h-[44px] sm:min-h-0 w-full"
            />
            <input
              type="text"
              name="event"
              placeholder="Rechercher par événement..."
              value={filters.event}
              onChange={handleFilterChange}
              className="px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 min-h-[44px] sm:min-h-0 w-full"
            />
            <input
              type="month"
              name="month"
              value={filters.month}
              onChange={handleFilterChange}
              className="px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 min-h-[44px] sm:min-h-0 w-full"
            />
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 mt-4 w-full overflow-hidden min-w-0">
        <div className="rounded-lg border border-gray-200 bg-white shadow overflow-hidden w-full h-full min-h-[400px]">
          <div className="overflow-x-auto overflow-y-auto h-[calc(100vh-220px)] min-h-[300px] w-full" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full min-w-[640px] border-collapse table-fixed" style={{ tableLayout: "fixed" }}>
              <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
                <tr>
                  <th scope="col" className="w-[10%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Prénom
                  </th>
                  <th scope="col" className="w-[10%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Nom
                  </th>
                  <th scope="col" className="w-[18%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Email
                  </th>
                  <th scope="col" className="w-[8%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Mois
                  </th>
                  <th scope="col" className="w-[10%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Jours Travaillés
                  </th>
                  <th scope="col" className="w-[10%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Total Heures
                  </th>
                  <th className="w-[14%] min-w-0 px-3 py-3 text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Statut
                  </th>
                  <th scope="col" className="w-[10%] min-w-0 px-3 py-3 text-sm text-right font-semibold text-gray-700 border-b border-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                  {filteredData.map((record, idx) => (
                    <tr key={record._id} className={idx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/50 hover:bg-gray-50"}>
                      <td className="px-3 py-3 text-sm font-medium text-gray-800 truncate min-w-0" title={record.agent?.firstName}>
                        {record.agent?.firstName || "N/A"}
                      </td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-800 truncate min-w-0" title={record.agent?.lastName}>
                        {record.agent?.lastName || "N/A"}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 truncate min-w-0" title={record.agent?.email}>
                        {record.agent?.email || "N/A"}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 truncate min-w-0">
                        {record.month || "N/A"}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 min-w-0">
                        {record.workingHours?.length || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 min-w-0">
                        {(() => {
                          if (!record.workingHours) return "N/A";

                          const totalMinutes = record.workingHours.reduce(
                            (acc, entry) => {
                              const { startTime, endTime } = entry;
                              if (startTime && endTime) {
                                let start = parse(
                                  startTime,
                                  "HH:mm",
                                  new Date()
                                );
                                let end = parse(endTime, "HH:mm", new Date());

                                if (end <= start) {
                                  end.setDate(end.getDate() + 1);
                                }

                                return acc + differenceInMinutes(end, start);
                              }
                              return acc;
                            },
                            0
                          );

                          const hours = Math.floor(totalMinutes / 60);
                          const minutes = totalMinutes % 60;
                          return `${hours}h ${minutes}m`;
                        })()}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-800 min-w-0">
                        <select
                          value={record.status}
                          onChange={(e) =>
                            handleStatusChange(record._id, e.target.value)
                          }
                          className={`w-full text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            record.status === "traité"
                              ? "text-green-700 bg-green-50 border-green-200"
                              : "text-gray-800"
                          }`}
                        >
                          <option value="en cours">En cours</option>
                          <option value="traité">Traité</option>
                        </select>
                      </td>

                      <td className="px-3 py-3 text-sm text-gray-800 text-right w-[10%] min-w-0">
                        <IconButton
                          onClick={(event) =>
                            handleMenuClick(event, record._id)
                          }
                          aria-label="Actions"
                          className="min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px]"
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={
                            Boolean(anchorEl) && selectedRecordId === record._id
                          }
                          onClose={handleMenuClose}
                        >
                          <MenuItem
                            onClick={() => {
                              toggleModal(record);
                              handleMenuClose();
                            }}
                          >
                            Voir Détails
                          </MenuItem>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500 bg-white border-t border-gray-100">
                Aucune donnée trouvée.
              </div>
            )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => toggleModal(null)}
        contentLabel="Détails de l'Agent"
        style={customStyles}
        ariaHideApp={false}
      >
        <div className="sticky top-0 z-10 flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Détails de l'Agent</h2>
          <button
            type="button"
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none transition-colors"
            onClick={() => toggleModal(null)}
            aria-label="Fermer"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(100vh-56px)]">
          {selectedRecord ? (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedRecord.agent?.firstName ?? "—"}{" "}
                      {selectedRecord.agent?.lastName ?? "—"}
                    </h3>
                    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                      <div>
                        <dt className="text-gray-500">Email</dt>
                        <dd className="font-medium text-gray-800">{selectedRecord.agent?.email ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Téléphone</dt>
                        <dd className="font-medium text-gray-800">{selectedRecord.agent?.telephone ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Poste</dt>
                        <dd className="font-medium text-gray-800">{selectedRecord.agent?.poste ?? "—"}</dd>
                      </div>
                    </dl>
                  </div>
                  <button
                    type="button"
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none transition-colors"
                    aria-label="Exporter en Excel"
                  >
                    <FaFileExport className="text-gray-500" />
                    Exporter en Excel
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-3">
                  Calendrier des événements
                </h3>
                {generateCalendar(
                  selectedRecord.month,
                  selectedRecord.workingHours ?? []
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Aucun détail disponible.</p>
          )}
        </div>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </main>
  );
};

export default Timesheet;
